import { Panel } from '@hover/blueprint';
import { hReset, iDownload } from '@hover/icons';
import { useCompositorControls } from '@hoverinc/rendering-compositor-react';
import React, { forwardRef, useContext } from 'react';

import { Component as Doors } from 'src/assets/icons/nav/doors.svg';
import { Component as InspireMe } from 'src/assets/icons/nav/inspire_me.svg';
import { Component as Reset } from 'src/assets/icons/nav/reset.svg';
import { Component as Roof } from 'src/assets/icons/nav/roof.svg';
import { Component as Trim } from 'src/assets/icons/nav/trim.svg';
import { Component as Versions } from 'src/assets/icons/nav/versions.svg';
import { Component as Walls } from 'src/assets/icons/nav/walls.svg';
import { Component as Templates } from 'src/assets/icons/nav/wand.svg';
import { Component as Windows } from 'src/assets/icons/nav/window.svg';
import { PoweredByHover } from 'src/modules/designStudio/components/DesignStudio/GuestMode/PoweredByHover';
import {
  LANDSCAPE_TOOLBAR_PADDING_FOR_LOGO,
  LANDSCAPE_TOOLBAR_WIDTH,
  TOOLBAR_HEIGHT,
} from 'src/modules/designStudio/components/Toolbar/toolBarConstants';
import { Menu } from 'src/modules/designStudio/constants';
import { useToolbarDisplaySettings } from 'src/modules/designStudio/hooks';
import {
  InteractiveRenderingStateContext,
  Mode,
  historyContext,
} from 'src/modules/designStudio/providers';
import { context } from 'src/modules/designStudio/providers/DesignStudioServiceProvider';
import { Feature } from 'src/shared/components/Feature';
import { FeatureFlag } from 'src/shared/constants';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import {
  TranslationNamespace,
  useIsLandscape,
  useMode,
  useTranslate,
} from 'src/shared/hooks';
import { Page, buttonPressed } from 'src/shared/telemetry';

import { ScrollContainer } from './ScrollContainer';
import { ToolbarButton } from './ToolbarButton';

interface PropTypes {
  menu: Menu | null;
  setMenu: (menu: Menu | null) => void;
}

interface InteractiveRenderingToolbarProps {
  menu: Menu | null;
  setMenu: (menu: Menu | null) => void;
}

const HorizontalDivider = () => (
  <Panel
    borderTopColor="neutral200"
    borderTopStyle="solid"
    borderTopWidth="0.5px"
    height="1px"
    margin={200}
    padding={0}
    width="80%"
  />
);

const Divider = ({ isLandscape }: { isLandscape: boolean }) =>
  isLandscape ? (
    <HorizontalDivider />
  ) : (
    <Panel
      borderRightColor="neutral200"
      borderRightStyle="solid"
      borderRightWidth="0.5px"
      height="64px"
      margin={200}
      padding={0}
      width="1px"
    />
  );

const InteractiveRenderingToolbar = forwardRef<
  HTMLDivElement,
  InteractiveRenderingToolbarProps
>(({ menu, setMenu }, ref) => {
  const { renderingName } = useContext(InteractiveRenderingStateContext);
  const { download, resetAllTextures } = useCompositorControls();
  const [isLandscape] = useIsLandscape();
  const mode = useMode();
  const shouldHaveMenuPaddingForLogo = mode === Mode.GUEST && isLandscape;
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

  return (
    <Panel
      borderRadius={0}
      bottom={0}
      height={isLandscape ? '100%' : `${TOOLBAR_HEIGHT}px`}
      id="toolbar"
      left={isLandscape ? 0 : undefined}
      marginX={0}
      padding={0}
      paddingLeft="env(safe-area-inset-left, 0)"
      paddingTop={
        shouldHaveMenuPaddingForLogo
          ? `${LANDSCAPE_TOOLBAR_PADDING_FOR_LOGO}px`
          : '0px'
      }
      position="absolute"
      ref={ref}
      style={{
        pointerEvents: 'auto',
      }}
      top={isLandscape ? 0 : undefined}
      width={isLandscape ? `${LANDSCAPE_TOOLBAR_WIDTH}px` : '100%'}
    >
      <ScrollContainer>
        <ToolbarButton
          active={menu === Menu.INTERACTIVE_RENDERING_WALL}
          icon={Walls}
          onClick={() => setMenu(Menu.INTERACTIVE_RENDERING_WALL)}
          text={t('menu.wall.title')}
        />
        <ToolbarButton
          active={menu === Menu.INTERACTIVE_RENDERING_ROOF}
          icon={Roof}
          onClick={() => setMenu(Menu.INTERACTIVE_RENDERING_ROOF)}
          text={t('menu.roof.title')}
        />
        <ToolbarButton
          active={menu === Menu.INTERACTIVE_RENDERING_TRIM}
          icon={Trim}
          onClick={() => setMenu(Menu.INTERACTIVE_RENDERING_TRIM)}
          text={t('menu.trim.title')}
        />
        <Divider isLandscape={isLandscape} />
        <ToolbarButton
          active={false}
          icon={iDownload}
          onClick={() => download(renderingName)}
          text={t('menu.download.title')}
        />
        <ToolbarButton
          active={false}
          icon={hReset}
          onClick={() => resetAllTextures()}
          text={t('menu.resetAll.title')}
        />
      </ScrollContainer>
      <PoweredByHover />
    </Panel>
  );
});

const Toolbar = forwardRef<HTMLDivElement, PropTypes>(
  ({ setMenu, menu }, ref) => {
    const [isLandscape] = useIsLandscape();
    const { displayVersionReset } = useToolbarDisplaySettings();
    const mode = useMode();
    const shouldHaveMenuPaddingForLogo = mode === Mode.GUEST && isLandscape;
    const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

    const { historyState, clearInspireMeImage } = useContext(historyContext);
    const { appService } = useContext(context);

    const appSnapshot = appService.getSnapshot
      ? appService.getSnapshot()
      : {
          context: {
            orgId: undefined,
            partnerId: undefined,
            userId: undefined,
          },
        };

    const setMenuAndClearInspireMeImage = (selectedMenu: Menu) => {
      clearInspireMeImage();
      setMenu(selectedMenu);
      if (selectedMenu === Menu.TEMPLATE) {
        buttonPressed({
          button_text: 'Design',
          primary_cta: false,
          job_id: historyState.jobId ?? undefined,
          org_id: appSnapshot.context.orgId,
          partner_id: appSnapshot.context.partnerId,
          user_id: appSnapshot.context.userId,
          user_email: historyState.email ?? undefined,
          page_or_screen_name: 'Design-studio',
          design_studio_mode: 'default',
          feature: 'design_templates',
          button_location: 'navigation-bar',
          track_id: 'nav-bar-design-button',
        });
      }
    };

    return (
      <Panel
        borderRadius={0}
        bottom={0}
        height={isLandscape ? '100%' : `${TOOLBAR_HEIGHT}px`}
        id="toolbar"
        left={isLandscape ? 0 : undefined}
        marginX={0}
        padding={0}
        paddingLeft="env(safe-area-inset-left, 0)"
        paddingTop={
          shouldHaveMenuPaddingForLogo
            ? `${LANDSCAPE_TOOLBAR_PADDING_FOR_LOGO}px`
            : '0px'
        }
        position="absolute"
        ref={ref}
        style={{
          pointerEvents: 'auto',
        }}
        top={isLandscape ? 0 : undefined}
        width={isLandscape ? `${LANDSCAPE_TOOLBAR_WIDTH}px` : '100%'}
      >
        <ScrollContainer>
          <Feature name={FeatureFlag.DesignStudio.DS_INSPIRE_ME}>
            <ToolbarButton
              active={menu === Menu.INSPIRE_ME}
              icon={InspireMe}
              onClick={() => {
                setMenu(Menu.INSPIRE_ME);
                buttonPressed({
                  button_text: t('menu.inspireMe.title'),
                  primary_cta: false,
                  job_id: historyState.jobId ?? undefined,
                  org_id: appSnapshot.context.orgId,
                  partner_id: appSnapshot.context.partnerId,
                  user_id: appSnapshot.context.userId,
                  user_email: historyState.email ?? undefined,
                  page_or_screen_name: Page.DESIGN_STUDIO,
                  design_studio_mode: Mode.DEFAULT,
                  feature: FeatureTracking.INSPIRE_ME,
                  button_location: 'navigation-bar',
                  track_id: 'nav-bar-inspiration-button',
                });
              }}
              text={t('menu.inspireMe.title')}
            />
          </Feature>
          <ToolbarButton
            active={menu === Menu.TEMPLATE}
            icon={Templates}
            onClick={() => setMenuAndClearInspireMeImage(Menu.TEMPLATE)}
            text="Design"
          />
          <ToolbarButton
            active={menu === Menu.WALL}
            icon={Walls}
            onClick={() => setMenuAndClearInspireMeImage(Menu.WALL)}
            text="Walls"
          />
          <ToolbarButton
            active={menu === Menu.ROOF}
            icon={Roof}
            onClick={() => setMenuAndClearInspireMeImage(Menu.ROOF)}
            text="Roof"
          />
          <ToolbarButton
            active={menu === Menu.TRIM}
            icon={Trim}
            onClick={() => setMenuAndClearInspireMeImage(Menu.TRIM)}
            text="Trim"
          />
          <ToolbarButton
            active={menu === Menu.WINDOW}
            icon={Windows}
            onClick={() => setMenuAndClearInspireMeImage(Menu.WINDOW)}
            text="Windows"
          />
          <ToolbarButton
            active={menu === Menu.DOOR}
            disabled={false}
            icon={Doors}
            onClick={() => setMenuAndClearInspireMeImage(Menu.DOOR)}
            text="Doors"
          />
          {displayVersionReset && <Divider isLandscape={isLandscape} />}
          {displayVersionReset && (
            <ToolbarButton
              active={menu === Menu.VERSION}
              disabled
              icon={Versions}
              onClick={() => setMenuAndClearInspireMeImage(Menu.VERSION)}
              text="Version"
            />
          )}
          {displayVersionReset && (
            <ToolbarButton
              active={menu === Menu.RESET}
              icon={Reset}
              onClick={() => setMenuAndClearInspireMeImage(Menu.RESET)}
              text="Reset"
            />
          )}
        </ScrollContainer>
        <PoweredByHover />
      </Panel>
    );
  },
);

export { Toolbar, InteractiveRenderingToolbar, TOOLBAR_HEIGHT };
